import { useServicesQuery } from '@/services/api';
import { useDesignStudioQuery } from '@/services/api/design-studio-api';
import type { ServicesApiRecentResource } from '@/services/api/services-api';

/**
 * Fetch recently edited resources for the current user.
 */
export function useRecents(workspaceId: number, count = 10) {
  return useServicesQuery(
    'getRecents',
    { params: { environment_id: workspaceId }, query: { count } },
    { staleTime: 60_000, enabled: workspaceId > 0 },
  );
}

/**
 * Fetch recently launched (shipped) resources across the workspace.
 */
export function useRecentlyShipped(workspaceId: number, count = 6) {
  return useServicesQuery(
    'getRecentlyShipped',
    { params: { environment_id: workspaceId }, query: { count } },
    { staleTime: 60_000, enabled: workspaceId > 0 },
  );
}

// ---------------------------------------------------------------------------
// Shared resource info resolution
// ---------------------------------------------------------------------------

export interface ResourceInfo {
  name: string | undefined;
  campaignType: string | undefined;
  dsNodeType: string | undefined;
  isLoading: boolean;
  /** True when the resource lookup failed (e.g. 404 — deleted resource). */
  isNotFound: boolean;
}

/**
 * Resolve resource name and metadata by fetching the individual resource endpoint.
 * Only the query matching `type` fires — the rest are disabled.
 */
export function useResourceInfo(
  workspaceId: number,
  type: ServicesApiRecentResource['type'],
  id: string,
): ResourceInfo {
  const options = { staleTime: 5 * 60 * 1000, retry: false };
  const numberId = Number(id);

  const campaign = useServicesQuery(
    'getCampaignById',
    { params: { environment_id: workspaceId, campaign_id: id } },
    { ...options, enabled: workspaceId > 0 && type === 'campaign' },
  );

  const newsletter = useServicesQuery(
    'getNewsletterById',
    { params: { environment_id: workspaceId, id: numberId } },
    { ...options, enabled: workspaceId > 0 && type === 'newsletter' },
  );

  const transactional = useServicesQuery(
    'getTransactionalMessageById',
    { params: { environment_id: workspaceId, transactional_message_id: numberId } },
    { ...options, enabled: workspaceId > 0 && type === 'transactional' },
  );

  const segment = useServicesQuery(
    'getSegmentById',
    { params: { environment_id: workspaceId, segment_id: numberId } },
    { ...options, enabled: workspaceId > 0 && type === 'segment' },
  );

  const inApp = useServicesQuery(
    'getInAppBroadcastById',
    { params: { environment_id: workspaceId, in_app_broadcast_id: numberId } },
    { ...options, enabled: workspaceId > 0 && type === 'in_app_broadcast' },
  );

  const snippet = useServicesQuery(
    'getSnippetById',
    { params: { environment_id: workspaceId, id: numberId } },
    { ...options, enabled: workspaceId > 0 && type === 'snippet' },
  );

  const dsNodes = useDesignStudioQuery(
    'getNodes',
    { params: { workspaceId } },
    { ...options, enabled: workspaceId > 0 && type === 'ds_node' },
  );

  let name: string | undefined;
  let campaignType: string | undefined;
  let dsNodeType: string | undefined;

  switch (type) {
    case 'campaign':
      name = campaign.data?.campaign?.name;
      campaignType = campaign.data?.campaign?.type;
      break;
    case 'newsletter':
      name = newsletter.data?.newsletter?.name;
      break;
    case 'transactional':
      name = transactional.data?.transactional_message?.name;
      break;
    case 'segment':
      name = segment.data?.segment?.name;
      break;
    case 'in_app_broadcast':
      name = inApp.data?.in_app_broadcast?.name;
      break;
    case 'snippet':
      name = snippet.data?.snippet?.name;
      break;
    case 'ds_node': {
      const dsNode = dsNodes.data?.find((node) => node.id === id);
      name = dsNode?.name;
      dsNodeType = dsNode?.type;
      break;
    }
    default: {
      const _exhaustive: never = type;
      name = String(_exhaustive);
    }
  }

  const isResourceLoading =
    (type === 'campaign' && campaign.isLoading) ||
    (type === 'newsletter' && newsletter.isLoading) ||
    (type === 'transactional' && transactional.isLoading) ||
    (type === 'segment' && segment.isLoading) ||
    (type === 'in_app_broadcast' && inApp.isLoading) ||
    (type === 'snippet' && snippet.isLoading) ||
    (type === 'ds_node' && dsNodes.isLoading);

  const isNotFound =
    (type === 'campaign' && campaign.isError) ||
    (type === 'newsletter' && newsletter.isError) ||
    (type === 'transactional' && transactional.isError) ||
    (type === 'segment' && segment.isError) ||
    (type === 'in_app_broadcast' && inApp.isError) ||
    (type === 'snippet' && snippet.isError) ||
    (type === 'ds_node' && (dsNodes.isError || (!dsNodes.isLoading && dsNodes.data != null && name == null)));

  return { name, campaignType, dsNodeType, isLoading: isResourceLoading, isNotFound };
}
